import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Home, Sun, Moon } from 'lucide-react'
import { apiUrl } from '../../config/api'
import useOrg from '../../hooks/useOrg'
import useTheme from '../../hooks/useTheme'
import Avatar from '../ui/Avatar'
import Tooltip from '../ui/Tooltip'

interface SidebarProps {
  onNavigate?: () => void
}

interface ChannelItem { id: number; name: string; slug: string; color: string | null }

export default function Sidebar({ onNavigate }: SidebarProps) {
  const org = useOrg()
  const { theme, toggleTheme } = useTheme()
  const { slug, channel } = useParams()
  const navigate = useNavigate()
  const [channels, setChannels] = useState<ChannelItem[]>([])
  const [userName, setUserName] = useState('')

  useEffect(() => {
    const token = localStorage.getItem('token')
    const user = localStorage.getItem('user')
    if (user) try { setUserName(JSON.parse(user).name || '') } catch {}
    if (!token) return
    fetch(apiUrl('/v1/community/my/channels'), {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.data) setChannels(d.data) })
      .catch(() => {})
  }, [])

  const go = (path: string) => {
    navigate(path)
    onNavigate?.()
  }

  return (
    <div className="w-sidebar h-full bg-[#E3E5E8] flex flex-col items-center py-3 gap-1">
      {/* Home */}
      <SidebarIcon
        icon={<Home size={20} />}
        label="Home"
        active={channel === 'dashboard'}
        onClick={() => go(`/${slug}/dashboard`)}
      />

      <div className="w-8 h-0.5 bg-black/10 rounded-full my-1" />

      {/* Canais de discussão reais */}
      <div className="flex-1 flex flex-col items-center gap-1 overflow-y-auto scrollbar-thin py-1">
        {channels.map((ch) => (
          <SidebarIcon
            key={ch.id}
            initials={ch.name.substring(0, 2).toUpperCase()}
            label={ch.name}
            color={ch.color || undefined}
            active={channel === ch.slug}
            onClick={() => go(`/${slug}/channels/${ch.slug}`)}
          />
        ))}
      </div>

      {/* Footer — theme + avatar */}
      <div className="mt-auto pt-2 flex flex-col items-center gap-2">
        <Tooltip label={theme === 'dark' ? 'Modo claro' : 'Modo escuro'}>
          <button
            onClick={toggleTheme}
            className="w-10 h-10 rounded-2xl flex items-center justify-center text-[#5C5F66] hover:text-[#2E3035] hover:bg-white/60 transition-colors"
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </Tooltip>
        <Tooltip label={userName || 'Perfil'}>
          <button onClick={() => go(`/${slug}/perfil`)}>
            <Avatar name={userName || 'U'} size="sm" isOnline />
          </button>
        </Tooltip>
      </div>
    </div>
  )
}

interface SidebarIconProps {
  initials?: string
  icon?: React.ReactNode
  label: string
  color?: string
  active?: boolean
  unreadCount?: number
  onClick: () => void
}

function SidebarIcon({ initials, icon, label, color, active, unreadCount, onClick }: SidebarIconProps) {
  return (
    <Tooltip label={label}>
      <div className="relative group">
        <button
          onClick={onClick}
          className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-200 cursor-pointer ${
            active ? 'rounded-xl' : 'hover:rounded-xl'
          }`}
          style={{
            backgroundColor: active ? (color || 'var(--color-primary)') : '#FFFFFF',
            color: active ? '#fff' : '#5C5F66',
          }}
        >
          {icon || <span className="text-xs font-bold">{initials}</span>}
        </button>

        {/* Indicator pill */}
        {active && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-1 h-10 bg-[#3F4147] rounded-r-full" />
        )}

        {/* Hover indicator */}
        {!active && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-1 h-0 group-hover:h-5 bg-[#3F4147] rounded-r-full transition-all duration-200" />
        )}

        {/* Unread badge */}
        {unreadCount !== undefined && unreadCount > 0 && !active && (
          <span className="absolute -bottom-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-[#EF4444] text-white text-[10px] font-bold px-1 border-2 border-[#E3E5E8]">
            {unreadCount}
          </span>
        )}
      </div>
    </Tooltip>
  )
}
